import { useRef, type MouseEvent } from 'react';
import type { CapiumTagGroup } from '@/core/models/capium-tags';
import './CapiumTagsPanel.scss';

interface CapiumTagsPanelProps {
  groups: CapiumTagGroup[];
  /** Called with a tag's raw `tag` string (e.g. `[period end date]`) when its link is clicked. */
  onInsert: (tag: string) => void;
  /** Width class for the panel's `form-field` host; defaults to the dialogs' `w-200`. */
  className?: string;
}

/** Inserts text at the cursor of the focused rich-text editor; false when no editor holds the cursor. */
function insertAtCursor(text: string): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (!active?.isContentEditable || typeof document.execCommand !== 'function') return false;
  try {
    return document.execCommand('insertText', false, text);
  } catch {
    return false;
  }
}

/** A mouse press on a tag must not move the cursor out of the editor, or the tag could not be placed where it was. */
const keepEditorFocus = (event: MouseEvent) => event.preventDefault();

/**
 * ap5-a5: the "Capium Tags" side panel shown beside a note editor - clicking a tag appends it to the wording being
 * edited. Every note editor (General Settings default notes, Report Settings notes, the additional notes dialogs and
 * Email Templates) renders this one panel, so none of them can drift to a shorter hand-built list again.
 *
 * AP parity: the list scrolls inside the panel (as the 3.0 tag tree did) so the full tag list does not stretch the
 * dialog it sits in.
 */
export function CapiumTagsPanel({ groups, onInsert, className = 'w-200' }: CapiumTagsPanelProps) {
  const lastClickRef = useRef<{ time: number; tag: string }>({ time: 0, tag: '' });

  const handleInsert = (tag: string, now: number) => {
    // AP parity: double-clicking fires click, click, dblclick. Suppress duplicate insertions of the same tag within 500ms.
    if (now - lastClickRef.current.time < 500 && lastClickRef.current.tag === tag) {
      return;
    }
    lastClickRef.current = { time: now, tag };
    // BUG-001: AP parity - the tag goes in at the cursor of the note being edited. The links keep focus in the editor (see
    // `keepEditorFocus`), so the browser inserts the text where the cursor is and fires the editor's own `input` event; when
    // no editor holds the cursor (the Title box, a plain field) the caller appends the tag to the wording as before.
    if (insertAtCursor(tag)) return;
    onInsert(tag);
  };

  return (
    <div className={`form-field ${className}`.trim()}>
      <p className="fw-bold">Capium Tags</p>
      <div className="capium-tags-list d-flex flex-column gap-10">
        {groups.map((group) => (
          <div key={group.category} className="d-flex flex-column gap-5">
            <p className="sub-ttl mb-0">{group.category}</p>
            {group.tags.map((item) => (
              <a
                key={item.tag}
                className="link hyperlink"
                title={item.tag}
                onMouseDown={keepEditorFocus}
                onClick={() => handleInsert(item.tag, Date.now())}
                onDoubleClick={() => handleInsert(item.tag, Date.now())}
              >
                {item.label}
              </a>
            ))}
          </div>
        ))}
      </div>
      <p className="tags-hint text-muted fs-12 mt-1 mb-0">* double click to add tag</p>
    </div>
  );
}
